import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import api from '../../utils/api'
import ConfirmModal from '../../components/modals/ConfirmModal'
import DoubleScrollContainer from '../../components/DoubleScrollContainer'

interface Project {
    _id: string; 
    name: string; 
    location: string; 
    priceRange?: string; 
    status?: string;
    projectStatus?: string; 
    projectType: string; 
    configuration?: string; 
    isActive: boolean; 
    createdAt: string;
    isOurPick: boolean;
    overview?: {
        priceRange?: string;
        configuration?: string;
    };
    developer?: { name: string; logo: string };
    developerDetails?: { name: string };
    slug?: string;
    homeSections?: {
        featuredProperties?: boolean;
        newlyLaunched?: boolean;
        categorySearch?: boolean;
    };
}

interface HomeSections {
    featuredProperties: boolean;
    newlyLaunched: boolean;
    categorySearch: boolean;
}

export default function AdminProjects() {
    const { token } = useAuth()
    const [projects, setProjects] = useState<Project[]>([])
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState<string | null>(null)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [search, setSearch] = useState('')
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [bulkDeleting, setBulkDeleting] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [confirmModal, setConfirmModal] = useState<{ 
        isOpen: boolean; 
        id: string | string[]; 
        name: string; 
        type: 'single' | 'bulk' 
    }>({ isOpen: false, id: '', name: '', type: 'single' });

    // Homepage section visibility state
    const [homeSections, setHomeSections] = useState<HomeSections>({
        featuredProperties: true,
        newlyLaunched: true,
        categorySearch: true
    });
    const [sectionDropdownOpen, setSectionDropdownOpen] = useState(false);
    const [savingSections, setSavingSections] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Per-project section dropdown
    const [openSectionDropdown, setOpenSectionDropdown] = useState<string | null>(null);
    const [savingProjectSection, setSavingProjectSection] = useState<string | null>(null);
    const perRowDropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setSectionDropdownOpen(false);
            }
            // Close per-project dropdowns too
            setOpenSectionDropdown(prev => {
                if (prev) {
                    const el = document.getElementById(`section-dd-${prev}`);
                    if (el && !el.contains(e.target as Node)) return null;
                }
                return prev;
            });
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Load current homepage section settings
    useEffect(() => {
        api.getSystemSettings().then(data => {
            if (data?.homeSections) {
                setHomeSections({
                    featuredProperties: data.homeSections.featuredProperties !== false,
                    newlyLaunched: data.homeSections.newlyLaunched !== false,
                    categorySearch: data.homeSections.categorySearch !== false,
                });
            }
        }).catch(console.error);
    }, []);

    const handleSectionToggle = (key: keyof HomeSections) => {
        setHomeSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSaveSections = async () => {
        if (!token) return;
        setSavingSections(true);
        try {
            await api.updateSystemSettings(token, { homeSections });
            setMsg({ type: 'success', text: 'Homepage sections updated successfully!' });
            setSectionDropdownOpen(false);
        } catch (err) {
            setMsg({ type: 'error', text: 'Failed to save section settings.' });
        } finally {
            setSavingSections(false);
        }
    };

    useEffect(() => {
        if (!token) { setLoading(false); return }
        api.adminGetAllProjects(token)
            .then(data => { if (Array.isArray(data)) setProjects(data) })
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [token])

    useEffect(() => {
        setCurrentPage(1)
    }, [search])

    const handleDelete = async () => {
        const { id } = confirmModal;
        if (!token || typeof id !== 'string') return
        setDeleting(id)
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
            const res = await api.adminDeleteProject(token, id)
            if (res.message === 'Project deleted') {
                setProjects(prev => prev.filter(p => p._id !== id))
                setMsg({ type: 'success', text: `Project deleted successfully.` })
            } else {
                setMsg({ type: 'error', text: res.message || 'Delete failed.' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error failed to delete project.' })
        }
        setDeleting(null)
    }

    const filtered = projects.filter(p => {
        const searchLower = search.toLowerCase();
        return (
            (p.name || '').toLowerCase().includes(searchLower) ||
            (p.location || '').toLowerCase().includes(searchLower) ||
            (p.developer?.name || '').toLowerCase().includes(searchLower)
        );
    })

    const totalPages = Math.ceil(filtered.length / itemsPerPage)
    const paginatedItems = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

    const isAllSelected = filtered.length > 0 && filtered.every(p => selectedIds.includes(p._id));

    const toggleSelectAll = () => {
        if (isAllSelected) {
            const filteredIdSet = new Set(filtered.map(p => p._id));
            setSelectedIds(prev => prev.filter(id => !filteredIdSet.has(id)));
        } else {
            const newIds = new Set([...selectedIds, ...filtered.map(p => p._id)]);
            setSelectedIds(Array.from(newIds));
        }
    }

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id])
    }

    const handleBulkDelete = async () => {
        const { id } = confirmModal;
        if (!token || !Array.isArray(id) || id.length === 0) return
        
        setBulkDeleting(true)
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
            const res = await api.adminBulkDeleteProjects(token, id)
            if (res.count > 0 || res.message?.includes('deleted')) {
                const deletedCount = res.count ?? id.length;
                setProjects(prev => prev.filter(p => !id.includes(p._id)))
                setMsg({ type: 'success', text: `${deletedCount} projects deleted successfully.` })
                setSelectedIds([])
                setCurrentPage(1)
            } else {
                setMsg({ type: 'error', text: res.message || 'Bulk delete failed.' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error failed to delete projects.' })
        }
        setBulkDeleting(false)
    }

    return (
        <AdminLayout>
            <div className="animate__animated animate__fadeIn">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                    <div>
                        <h1 className="text-3xl font-bold text-[#05144b] mb-2 leading-none">Project Inventory</h1>
                        <p className="text-[#05144b] font-semibold text-[14px]">Manage and monitor property listings across the platform.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Homepage Sections Dropdown */}
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setSectionDropdownOpen(prev => !prev)}
                                className="inline-flex items-center gap-2 px-5 py-3.5 bg-white border-2 border-[#05144b]/20 text-[#05144b] hover:border-[#05144b] text-[12px] md:text-[14px] font-bold rounded-md transition-all shadow-sm active:scale-95"
                            >
                                <i className="fa-solid fa-layer-group text-xs"></i>
                                Homepage Sections
                                <i className={`fa-solid fa-chevron-down text-[10px] transition-transform ${sectionDropdownOpen ? 'rotate-180' : ''}`}></i>
                            </button>

                            {sectionDropdownOpen && (
                                <div className="absolute right-0 top-full mt-2 w-[300px] bg-white border border-gray-100 rounded-xl shadow-2xl z-50 overflow-hidden">
                                    {/* Dropdown Header */}
                                    <div className="px-5 py-4 border-b border-gray-100 bg-[#05144b]">
                                        <p className="text-white font-black text-[13px] flex items-center gap-2">
                                            <i className="fa-solid fa-home text-amber-400"></i>
                                            Homepage Section Visibility
                                        </p>
                                        <p className="text-white/50 text-[11px] font-medium mt-0.5">Toggle sections shown on the home page</p>
                                    </div>

                                    {/* Checkbox Items */}
                                    <div className="p-4 space-y-2">
                                        {[
                                            { key: 'featuredProperties' as const, label: 'Featured Properties', icon: 'fa-star', color: 'text-amber-500' },
                                            { key: 'newlyLaunched' as const, label: 'Newly Launched Projects', icon: 'fa-rocket', color: 'text-blue-500' },
                                            { key: 'categorySearch' as const, label: 'WHAT ARE YOU LOOKING FOR?', icon: 'fa-magnifying-glass', color: 'text-emerald-500' },
                                        ].map(item => (
                                            <label
                                                key={item.key}
                                                className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all border-2 ${homeSections[item.key] ? 'border-[#05144b]/20 bg-[#05144b]/5' : 'border-gray-100 bg-gray-50/50'} hover:border-[#05144b]/30`}
                                                onClick={() => handleSectionToggle(item.key)}
                                            >
                                                <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all flex-shrink-0 ${homeSections[item.key] ? 'bg-[#05144b] border-[#05144b]' : 'border-gray-300 bg-white'}`}>
                                                    {homeSections[item.key] && <i className="fa-solid fa-check text-white text-[9px]"></i>}
                                                </div>
                                                <i className={`fa-solid ${item.icon} ${item.color} text-sm`}></i>
                                                <span className="text-[12px] font-bold text-[#05144b] leading-tight">{item.label}</span>
                                            </label>
                                        ))}
                                    </div>

                                    {/* Save Button */}
                                    <div className="px-4 pb-4">
                                        <button
                                            onClick={handleSaveSections}
                                            disabled={savingSections}
                                            className="w-full py-2.5 bg-[#05144b] text-white font-black text-[12px] rounded-lg hover:bg-black transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-60"
                                        >
                                            {savingSections ? (
                                                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Saving...</>
                                            ) : (
                                                <><i className="fa-solid fa-floppy-disk"></i> Save Changes</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <Link to="/admin-add-project" className="inline-flex items-center gap-3 px-6 py-3.5 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 text-[12px] md:text-[14px] font-bold rounded-md transition-all shadow-lg active:scale-95">
                            <i className="fa-solid fa-plus text-xs"></i>
                            Add Project
                        </Link>
                    </div>
                </div>

                {/* Notification */}
                {msg && (
                    <div className={`mb-8 p-5 rounded-md border border-gray-100 animate-in slide-in-from-top-4 duration-500 shadow-sm ${msg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                        <div className="flex items-center gap-4 font-bold text-[13px]">
                            <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'} text-base`}></i>
                            {msg.text}
                        </div>
                    </div>
                )}

                {/* Main Table Container */}
                <div className="bg-white dark:bg-slate-900 rounded-md border border-gray-100 dark:border-slate-800 shadow-sm overflow-hidden">
                    {/* Table Header / Toolbar */}
                    <div className="p-5 border-b border-gray-100 dark:border-slate-800 flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-white/50 dark:bg-slate-800/30">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white border-2 border-[#05144b] text-[#05144b] rounded-md flex items-center justify-center shadow-lg">
                                <i className="fa-solid fa-layer-group text-lg"></i>
                            </div>
                            <div>
                                <h5 className="text-xl md:text-2xl font-bold text-[#05144b] leading-none">Live Projects</h5>
                                <p className="text-[13px] md:text-[14px] font-bold text-[#05144b] mt-1.5">{filtered.length} listings identified</p>
                            </div>
                        </div>

                        <div className="relative group max-w-lg w-full">
                            <i className="fa-solid fa-magnifying-glass absolute left-5 top-1/2 -translate-y-1/2 text-[#05144b]/30 group-focus-within:text-[#05144b] transition-colors text-xs"></i>
                            <input
                                type="text"
                                className="block w-full pl-14 pr-6 py-3.5 bg-white border border-gray-100 rounded-md text-[14px] md:text-[15px] font-bold focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] transition-all text-[#05144b] placeholder:text-gray-300 shadow-sm"
                                placeholder="Search inventory..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>

                        {selectedIds.length > 0 && (
                            <button 
                                onClick={() => setConfirmModal({ isOpen: true, id: selectedIds, name: `${selectedIds.length} projects`, type: 'bulk' })}
                                disabled={bulkDeleting}
                                className="inline-flex items-center gap-2 px-6 py-3.5 bg-rose-600 hover:bg-rose-700 text-white text-[12px] md:text-[14px] font-bold rounded-md transition-all shadow-xl active:scale-95 disabled:opacity-50"
                            >
                                {bulkDeleting ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                    <i className="fa-solid fa-trash-can"></i>
                                )}
                                Delete Selected ({selectedIds.length})
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-32">
                            <div className="w-10 h-10 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-6"></div>
                            <p className="text-[#05144b] font-bold text-[14px]">Accessing records...</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-32 px-6 text-center">
                            <div className="w-20 h-20 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center mb-6 border-2 border-gray-100 dark:border-slate-700">
                                <i className="fa-solid fa-building-circle-exclamation text-3xl text-[#05144b]/10"></i>
                            </div>
                            <h5 className="text-xl font-bold text-[#05144b] mb-2">
                                {search ? `No results for "${search}"` : 'Inventory is empty'}
                            </h5>
                            <p className="text-[#05144b]/60 text-[14px] font-bold">Adjust filters or add a new project asset.</p>
                        </div>
                    ) : (
                        <DoubleScrollContainer>
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-white/30">
                                    <tr>
                                        <th className="px-3 py-4 border-b border-gray-100">
                                            <input 
                                                type="checkbox" 
                                                className="w-4 h-4 rounded border-gray-300 text-[#05144b] focus:ring-[#05144b] cursor-pointer"
                                                checked={isAllSelected}
                                                onChange={toggleSelectAll}
                                            />
                                        </th>
                                        <th className="px-2 py-4 text-[12px] md:text-[13px] font-bold text-[#05144b] border-b border-gray-100">Project Details</th>
                                        <th className="px-2 py-4 text-[12px] md:text-[13px] font-bold text-[#05144b] border-b border-gray-100">Locality</th>
                                        <th className="px-2 py-4 text-[12px] md:text-[13px] font-bold text-[#05144b] border-b border-gray-100">Status</th>
                                        <th className="px-2 py-4 text-[12px] md:text-[13px] font-bold text-[#05144b] border-b border-gray-100">Featured</th>
                                        <th className="px-2 py-4 text-[12px] md:text-[13px] font-bold text-[#05144b] border-b border-gray-100">Homepage Sections</th>
                                        <th className="px-2 py-4 text-[12px] md:text-[13px] font-bold text-[#05144b] border-b border-gray-100">Developer</th>
                                        <th className="px-3 py-4 text-right text-[12px] md:text-[13px] font-bold text-[#05144b] border-b border-gray-100">Management</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100/50 dark:divide-slate-800/50">
                                    {paginatedItems.map(p => {
                                        const displayConstructionStatus = p.projectStatus || 'Pending';
                                        const displayPrice = p.priceRange || p.overview?.priceRange || 'Price on request';
                                        const displayConfig = p.configuration || p.overview?.configuration || 'N/A';
                                        const currentStatus = p.status || 'Pending';
                                        
                                        const handleStatusUpdate = async (newStatus: string) => {
                                            if (!token) return;
                                            try {
                                                const res = await api.adminUpdateProject(token, p._id, { status: newStatus });
                                                if (res._id) {
                                                    setProjects(prev => prev.map(proj => proj._id === p._id ? { ...proj, status: newStatus } : proj));
                                                    setMsg({ type: 'success', text: `Status updated to ${newStatus}` });
                                                } else {
                                                    setMsg({ type: 'error', text: res.message || 'Verification failed' });
                                                }
                                            } catch (err) {
                                                setMsg({ type: 'error', text: 'Critical structure error' });
                                            }
                                        };

                                        return (
                                            <tr key={p._id} className={`hover:bg-white/80 dark:hover:bg-slate-800/30 transition-all group ${selectedIds.includes(p._id) ? 'bg-blue-50/50' : ''}`}>
                                                <td className="px-3 py-4 whitespace-nowrap">
                                                    <input 
                                                        type="checkbox" 
                                                        className="w-4 h-4 rounded border-gray-300 text-[#05144b] focus:ring-[#05144b] cursor-pointer"
                                                        checked={selectedIds.includes(p._id)}
                                                        onChange={() => toggleSelect(p._id)}
                                                    />
                                                </td>
                                                <td className="px-2 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-11 h-11 bg-white border-2 border-[#05144b] text-[#05144b] rounded-md flex items-center justify-center transition-all shadow-sm">
                                                            <i className="fa-solid fa-city text-sm"></i>
                                                        </div>
                                                        <div>
                                                            <div className="text-[15px] md:text-[16px] font-bold text-black group-hover:translate-x-1 transition-transform">{p.name}</div>
                                                            <div className="text-[12px] font-bold text-black/60 mt-1">{displayConfig}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-2 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2.5">
                                                        <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center border border-gray-100">
                                                            <i className="fa-solid fa-map-location-dot text-[9px] text-black"></i>
                                                        </div>
                                                        <span className="text-[13px] font-bold text-black">{p.location || 'N/A'}</span>
                                                    </div>
                                                </td>
                                                <td className="px-2 py-4 whitespace-nowrap">
                                                    <div className="flex flex-col gap-1.5">
                                                        <div className="relative inline-block w-fit">
                                                            <select 
                                                                value={currentStatus}
                                                                onChange={(e) => handleStatusUpdate(e.target.value)}
                                                                className={`appearance-none pl-8 pr-10 py-2 rounded-xl text-[12px] font-bold transition-all cursor-pointer outline-none
                                                                    ${currentStatus === 'Approved' ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 
                                                                    currentStatus === 'Rejected' ? 'bg-rose-50 border-rose-100 text-rose-600' : 
                                                                    'bg-amber-50 border-amber-100 text-amber-600'}`}
                                                            >
                                                                <option value="Pending">Waiting</option>
                                                                <option value="Approved">Verified</option>
                                                                <option value="Rejected">Flagged</option>
                                                            </select>
                                                            <div className={`absolute left-3 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full
                                                                ${currentStatus === 'Approved' ? 'bg-emerald-500' : 
                                                                currentStatus === 'Rejected' ? 'bg-rose-500' : 
                                                                'bg-amber-500'}`}>
                                                            </div>
                                                            <i className="fa-solid fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-[8px] opacity-30 pointer-events-none"></i>
                                                        </div>
                                                        <span className="text-[12px] font-medium text-black/40 ml-1">{displayConstructionStatus}</span>
                                                    </div>
                                                </td>
                                                <td className="px-2 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-3">
                                                        <label className="relative inline-flex items-center cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                className="sr-only peer"
                                                                checked={p.isOurPick}
                                                                onChange={async (e) => {
                                                                    if (!token) return;
                                                                    const newValue = e.target.checked;
                                                                    try {
                                                                        const res = await api.adminUpdateProject(token, p._id, { isOurPick: newValue });
                                                                        if (res._id) {
                                                                            setProjects(prev => prev.map(proj => proj._id === p._id ? { ...proj, isOurPick: newValue } : proj));
                                                                            setMsg({ type: 'success', text: `Project ${newValue ? 'marked as Featured' : 'removed from Featured'}` });
                                                                        }
                                                                    } catch (err) {
                                                                        setMsg({ type: 'error', text: 'Failed to update featured status' });
                                                                    }
                                                                }}
                                                            />
                                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#f1ae24]"></div>
                                                        </label>
                                                        <span className={`text-[12px] font-bold ${p.isOurPick ? 'text-[#f1ae24]' : 'text-slate-400'}`}>
                                                            {p.isOurPick ? 'Featured' : 'Regular'}
                                                        </span>
                                                    </div>
                                                 </td>

                                                {/* Homepage Sections column */}
                                                <td className="px-2 py-4 whitespace-nowrap">
                                                    <div id={`section-dd-${p._id}`} className="relative">
                                                        {/* Trigger button showing active sections */}
                                                        <button
                                                            onClick={() => setOpenSectionDropdown(prev => prev === p._id ? null : p._id)}
                                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-[11px] font-bold transition-all ${
                                                                (p.homeSections?.featuredProperties || p.homeSections?.newlyLaunched || p.homeSections?.categorySearch)
                                                                    ? 'border-[#05144b]/30 bg-[#05144b]/5 text-[#05144b]'
                                                                    : 'border-gray-100 bg-gray-50 text-gray-400'
                                                            }`}
                                                        >
                                                            <i className="fa-solid fa-layer-group text-[10px]"></i>
                                                            <span>
                                                                {[p.homeSections?.featuredProperties && '★', p.homeSections?.newlyLaunched && '🚀', p.homeSections?.categorySearch && '🔍'].filter(Boolean).join(' ') || 'None'}
                                                            </span>
                                                            <i className={`fa-solid fa-chevron-down text-[8px] transition-transform ${openSectionDropdown === p._id ? 'rotate-180' : ''}`}></i>
                                                        </button>

                                                        {openSectionDropdown === p._id && (
                                                            <div className="absolute left-0 top-full mt-1 w-[240px] bg-white border border-gray-100 rounded-xl shadow-2xl z-50 overflow-hidden">
                                                                <div className="px-4 py-3 border-b border-gray-100 bg-[#05144b]">
                                                                    <p className="text-white font-black text-[11px] uppercase tracking-widest">Homepage Sections</p>
                                                                    <p className="text-white/40 text-[10px] mt-0.5">Select where this project appears</p>
                                                                </div>
                                                                <div className="p-3 space-y-1.5">
                                                                    {[
                                                                        { key: 'featuredProperties' as const, label: 'Featured Properties', icon: '⭐' },
                                                                        { key: 'newlyLaunched' as const, label: 'Newly Launched', icon: '🚀' },
                                                                        { key: 'categorySearch' as const, label: 'What Are You Looking For?', icon: '🔍' },
                                                                    ].map(item => {
                                                                        const isChecked = !!p.homeSections?.[item.key];
                                                                        return (
                                                                            <label
                                                                                key={item.key}
                                                                                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-all border ${
                                                                                    isChecked ? 'border-[#05144b]/20 bg-[#05144b]/5' : 'border-gray-50 hover:bg-gray-50'
                                                                                }`}
                                                                                onClick={async () => {
                                                                                    if (!token || savingProjectSection === p._id) return;
                                                                                    const newVal = !isChecked;
                                                                                    const newSections = { ...p.homeSections, [item.key]: newVal };
                                                                                    setSavingProjectSection(p._id);
                                                                                    try {
                                                                                        const res = await api.adminUpdateProject(token, p._id, { homeSections: newSections });
                                                                                        if (res._id) {
                                                                                            setProjects(prev => prev.map(proj => proj._id === p._id ? { ...proj, homeSections: newSections } : proj));
                                                                                        }
                                                                                    } catch (err) {
                                                                                        setMsg({ type: 'error', text: 'Failed to update sections' });
                                                                                    } finally {
                                                                                        setSavingProjectSection(null);
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                                                                    isChecked ? 'bg-[#05144b] border-[#05144b]' : 'border-gray-300 bg-white'
                                                                                }`}>
                                                                                    {isChecked && <i className="fa-solid fa-check text-white text-[8px]"></i>}
                                                                                </div>
                                                                                <span className="text-[11px]">{item.icon}</span>
                                                                                <span className="text-[11px] font-bold text-[#05144b]">{item.label}</span>
                                                                                {savingProjectSection === p._id && <div className="ml-auto w-3 h-3 border-2 border-[#05144b] border-t-transparent rounded-full animate-spin"></div>}
                                                                            </label>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>

                                                <td className="px-2 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2.5">
                                                        <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center border border-gray-100">
                                                            <i className="fa-solid fa-building-columns text-[9px] text-black"></i>
                                                        </div>
                                                        <span className="text-[13px] font-bold text-black">{p.developerDetails?.name || p.developer?.name || 'Private owner'}</span>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-4 whitespace-nowrap text-right">
                                                    <div className="flex justify-end items-center gap-2">
                                                         <Link to={`/project-details/${p.slug || p._id}`} target="_blank" className="w-9 h-9 rounded-xl bg-white border-2 border-gray-100 text-black hover:border-[#05144b] hover:text-[#05144b] transition-all flex items-center justify-center active:scale-90" title="Live Preview">
                                                            <i className="fa-solid fa-arrow-up-right-from-square text-xs"></i>
                                                        </Link>
                                                         <Link to={`/admin-edit-project/${p._id}`} className="w-9 h-9 rounded-xl bg-white border-2 border-gray-100 text-black hover:border-[#05144b] hover:text-[#05144b] transition-all flex items-center justify-center active:scale-90" title="Edit Parameters">
                                                            <i className="fa-solid fa-pen-nib text-xs"></i>
                                                        </Link>
                                                        <button
                                                            disabled={deleting === p._id}
                                                            onClick={() => setConfirmModal({ isOpen: true, id: p._id, name: p.name, type: 'single' })}
                                                            className="w-9 h-9 rounded-xl bg-white border-2 border-gray-100 text-black hover:border-rose-600 hover:text-rose-600 transition-all flex items-center justify-center disabled:opacity-50 active:scale-90"
                                                            title="Decommission Asset"
                                                        >
                                                            {deleting === p._id
                                                                ? <div className="w-4 h-4 border-2 border-rose-600 border-t-transparent rounded-full animate-spin"></div>
                                                                : <i className="fa-solid fa-trash-can text-xs"></i>
                                                            }
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </DoubleScrollContainer>
                    )}

                    {/* Pagination Controls */}
                    {!loading && filtered.length > 0 && (
                        <div className="px-5 py-5 border-t border-gray-100 dark:border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4 bg-white/50">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                <p className="text-[13px] font-bold text-black/50">
                                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length} projects
                                </p>
                                <div className="flex items-center gap-2">
                                    <span className="text-[13px] font-bold text-black/50">Show</span>
                                    <div className="relative">
                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) => {
                                                setItemsPerPage(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="appearance-none pl-3 pr-8 py-1.5 bg-white border border-gray-200 text-[#05144b] text-[12px] font-bold rounded-lg cursor-pointer outline-none transition-all focus:border-[#05144b] shadow-sm"
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                        <i className="fa-solid fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-[8px] text-[#05144b] pointer-events-none opacity-50"></i>
                                    </div>
                                    <span className="text-[13px] font-bold text-black/50">projects per page</span>
                                </div>
                            </div>
                            
                            {totalPages > 1 && (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                         className="px-4 py-2 text-[12px] font-bold text-black bg-white border border-gray-100 rounded-md hover:border-[#05144b] hover:text-[#05144b] transition-all disabled:opacity-30 disabled:pointer-events-none"
                                    >
                                        <i className="fa-solid fa-chevron-left mr-2"></i>
                                        Previous
                                    </button>
                                    
                                    <div className="flex items-center gap-1">
                                        {[...Array(totalPages)].map((_, i) => {
                                            const pageNum = i + 1;
                                            if (totalPages > 5 && pageNum !== 1 && pageNum !== totalPages && Math.abs(pageNum - currentPage) > 1) {
                                                if (pageNum === 2 || pageNum === totalPages - 1) return <span key={pageNum} className="px-2 text-black/20">...</span>;
                                                return null;
                                            }
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`w-9 h-9 flex items-center justify-center rounded-md text-[12px] font-bold transition-all
                                                        ${currentPage === pageNum ? 'bg-white text-[#05144b] border-2 border-[#05144b] shadow-lg' : 'text-black hover:bg-gray-50 border border-transparent hover:border-gray-100'}`}
                                                >
                                                    {pageNum}
                                                </button>
                                            )
                                        })}
                                    </div>

                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                         className="px-4 py-2 text-[12px] font-bold text-black bg-white border border-gray-100 rounded-md hover:border-[#05144b] hover:text-[#05144b] transition-all disabled:opacity-30 disabled:pointer-events-none"
                                    >
                                        Next
                                        <i className="fa-solid fa-chevron-right ml-2"></i>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <ConfirmModal 
                isOpen={confirmModal.isOpen}
                title={confirmModal.type === 'bulk' ? 'Bulk Delete Projects' : 'Delete Project'}
                message={`Are you sure you want to delete ${confirmModal.name}? This action cannot be undone.`}
                onConfirm={confirmModal.type === 'bulk' ? handleBulkDelete : handleDelete}
                onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                confirmText={confirmModal.type === 'bulk' ? 'Bulk Delete' : 'Delete Asset'}
                cancelText="Cancel"
                type="danger"
            />
        </AdminLayout>
    )
}
